import React, { useState, useEffect } from 'react';
import {
  X, Calendar, Clock, FileText, AlertTriangle,
  CheckCircle, XCircle, Wrench, Info, ChevronRight, Trash2, User, Pencil,
  ShieldCheck, Lock, Loader2
} from 'lucide-react';
// FT-186: Removed Edit2, Save - inline editing replaced by "Edit Job" button
import { getJobDescription } from '../../../../config/maintenanceJobDescriptions';
import { getJobDisplayName } from '../../../../utils/maintenanceJobHelpers';
// FT-186: Removed updateJobDescription - inline editing replaced by "Edit Job" button
import MaintenanceCheckForm from './MaintenanceCheckForm';
import QuickAddEquipmentModal from './QuickAddEquipmentModal';
import MaintenanceCompletionReport from './MaintenanceCompletionReport';
import { useCrew } from '../../../../hooks/useCrew';
// FT-221: Removed resolveCrewName - name read directly from crew object at selection time

const MaintenanceCheckModal = ({
  isOpen,
  onClose,
  job,
  onSave,
  equipment = [],
  onAddEquipment,
  onViewHistory,
  onDelete,
  onEdit,                   // FT-186: Handler to open edit modal
  completionHistory = [],  // Recent completions for this job
  vesselName = '',          // For audit trail display
  canSignOff = false,       // FT-394: skipper+ can sign off completions
  onSignOff,                // FT-394: (recordId) => void
  isSigningOff = false      // FT-394: loading state
}) => {
  // FT-186: Removed isEditing state - inline editing replaced by "Edit Job" button in header
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedCompletion, setSelectedCompletion] = useState(null);
  const [showCompletionReport, setShowCompletionReport] = useState(false);

  // Check if this is a custom job that can be deleted
  // Custom jobs have: custom: true, type: 'custom', id includes ':custom:', or category === 'custom'
  const isCustomJob = job?.custom === true ||
                      job?.type === 'custom' ||
                      job?.id?.includes(':custom:') ||
                      job?.category === 'custom';
  // FT-186: Removed editedDescription state - inline editing replaced by "Edit Job" button in header
  const [jobDescription, setJobDescription] = useState({});
  const [showAddEquipment, setShowAddEquipment] = useState(false);

  // FT-91: Get crew data for name resolution in completion reports
  const { crew } = useCrew();

  // FT-221: Use performerName if present (saved at write time), else 'Not recorded'
  // Do NOT use resolveCrewName() - it may fail if crew list not loaded
  const getCrewDisplayName = (crewId, record = null) => {
    // FT-221: Check for pre-resolved performerName first (new records)
    if (record?.performerName) {
      return record.performerName;
    }
    // Legacy fallback: show 'Not recorded' (no resolver, no UUID display)
    return 'Not recorded';
  };

  // Completion form data
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    inspector: '',
    allUnitsChecked: true,
    defectsFound: false,
    notes: '',
    defectDetails: '',
    detailedMode: false,
    equipmentChecks: {}
  });

  // Load job description
  useEffect(() => {
    if (job) {
      const description = getJobDescription(getJobDisplayName(job));
      setJobDescription(description);
      // FT-186: Removed setEditedDescription - inline editing replaced by "Edit Job" button
    }
  }, [job]);

  // FT-78 Fix: Filter equipment by linkedEquipmentIds (same pattern as MaintenanceCheckView)
  // This ensures only equipment explicitly linked to the job is shown
  const resolveJobEquipment = (job, equipmentList = []) => {
    const ids = Array.isArray(job?.linkedEquipmentIds) ? job.linkedEquipmentIds : [];
    if (ids.length === 0) return [];
    const idSet = new Set(ids);
    return equipmentList.filter(eq => eq && idSet.has(eq.id));
  };

  const relevantEquipment = resolveJobEquipment(job, equipment);

  // Initialize equipment checks when switching to detailed mode
  useEffect(() => {
    if (relevantEquipment.length > 0 && formData.detailedMode) {
      const checks = {};
      relevantEquipment.forEach(item => {
        checks[item.id] = {
          checked: false,
          status: 'ok',
          notes: ''
        };
      });
      setFormData(prev => ({ ...prev, equipmentChecks: checks }));
    }
  }, [formData.detailedMode]);

  if (!isOpen || !job) return null;

  // FT-186: Removed handleSaveDescription - inline editing replaced by "Edit Job" button

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.inspector) {
      alert('Please enter inspector name');
      return;
    }

    // FT-23 Fix: Use the form's date field for completedAt, not today
    // This allows backdating completions and calculating nextDue correctly
    const completionDate = formData.date
      ? new Date(formData.date + 'T12:00:00').toISOString()
      : new Date().toISOString();

    onSave({
      ...formData,
      jobId: job.id,
      completedAt: completionDate,
      inspectorName: formData.inspectorName  // FT-221: Already captured at selection time
    });

    // Reset form
    setFormData({
      date: new Date().toISOString().split('T')[0],
      inspector: '',
      allUnitsChecked: true,
      defectsFound: false,
      notes: '',
      defectDetails: '',
      detailedMode: false,
      equipmentChecks: {}
    });
    
    onClose();
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Get status color
  const getStatusColor = () => {
    if (job.status === 'overdue') return 'text-red-600 bg-red-50';
    if (job.status === 'due_soon') return 'text-yellow-600 bg-yellow-50';
    return 'text-green-600 bg-green-50';
  };

  // Handle viewing a completion record
  const handleViewCompletion = (completion) => {
    // Enrich completion with vessel name for display
    const enrichedCompletion = {
      ...completion,
      vesselName: vesselName || completion.vesselName
    };
    setSelectedCompletion(enrichedCompletion);
    setShowCompletionReport(true);
  };

  // Handle editing a completion record from the completion report
  const handleEditCompletionFromModal = (completion, reason) => {
    // Log for now - full edit form in future phase
    console.log('[MaintenanceCheckModal] Edit requested:', completion.id, 'Reason:', reason);
    alert(`Edit reason captured: "${reason}"\n\nNote: Full edit form coming in a future update.`);
  };

  const checklistItems = jobDescription.checklist || [];

  return (
    <>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="fixed inset-0 bg-black bg-opacity-25" onClick={onClose} />
        
        <div className="relative min-h-screen flex items-center justify-center p-4">
          <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">
                  {getJobDisplayName(job)} Maintenance
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Complete maintenance check and record details
                </p>
              </div>
              <div className="flex items-center gap-2">
                {/* FT-186: Edit Job button for custom jobs - opens full edit modal */}
                {isCustomJob && onEdit && (
                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      onEdit(job);
                    }}
                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    title="Edit job details"
                  >
                    <Pencil className="h-4 w-4 mr-1.5" />
                    Edit Job
                  </button>
                )}
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="overflow-y-auto" style={{ maxHeight: 'calc(90vh - 140px)' }}>
              <form onSubmit={handleSubmit}>
                <div className="p-6 space-y-6">
                  {/* Job Information Section */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h4 className="text-lg font-medium text-gray-900 flex items-center">
                          <Wrench className="h-5 w-5 mr-2 text-gray-400" />
                          Job Description
                        </h4>
                        {/* FT-186: Description is now read-only here. Edit via "Edit Job" button in header */}
                        <p className="mt-1 text-sm text-gray-600">{jobDescription.description || job.checkType}</p>
                      </div>
                    </div>

                    {/* Status and Schedule - FT-183: Changed to 2 cols mobile, 4 cols desktop */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      <div>
                        <h5 className="text-xs font-medium text-gray-500 uppercase mb-1">Status</h5>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor()}`}>
                          {job.status === 'overdue' && <AlertTriangle className="h-3 w-3 mr-1" />}
                          {job.status === 'due_soon' && <Clock className="h-3 w-3 mr-1" />}
                          {job.status === 'compliant' && <CheckCircle className="h-3 w-3 mr-1" />}
                          {job.status === 'overdue'
                            ? `${Math.abs(job.daysUntilDue)} days overdue`
                            : job.status === 'due_soon'
                            ? `Due in ${job.daysUntilDue} days`
                            : 'Compliant'
                          }
                        </span>
                      </div>
                      <div>
                        <h5 className="text-xs font-medium text-gray-500 uppercase mb-1">Frequency</h5>
                        <p className="text-sm text-gray-900">
                          Every {job.interval} {job.unit}
                        </p>
                      </div>
                      <div>
                        <h5 className="text-xs font-medium text-gray-500 uppercase mb-1">Last Completed</h5>
                        <p className="text-sm text-gray-900">
                          {formatDate(job.lastCompleted)}
                        </p>
                      </div>
                      {/* FT-183: Assigned to - only show if assigned */}
                      {job.assignedTo && (
                        <div>
                          <h5 className="text-xs font-medium text-gray-500 uppercase mb-1 flex items-center gap-1">
                            <User className="h-3 w-3" />
                            Assigned To
                          </h5>
                          <p className="text-sm text-gray-900">{job.assignedTo}</p>
                        </div>
                      )}
                    </div>

                    {/* Regulation Info */}
                    {(job.regulation || job.certificate) && (
                      <div className="mt-3 pt-3 border-t border-gray-200 flex items-center gap-4 text-xs">
                        {job.regulation && (
                          <span className="flex items-center text-gray-600">
                            <FileText className="h-3 w-3 mr-1" />
                            {job.regulation}
                          </span>
                        )}
                        {job.certificate && (
                          <span className="flex items-center text-blue-600">
                            <Info className="h-3 w-3 mr-1" />
                            {job.certificate}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Checklist Section - FT-186: Now read-only. Edit via "Edit Job" button in header */}
                  {checklistItems.length > 0 && (
                    <div className="border rounded-lg p-4">
                      <h4 className="font-medium text-gray-900 mb-3">Inspection Checklist</h4>
                      <ul className="space-y-2">
                        {checklistItems.map((item, index) => (
                          <li key={index} className="flex items-start">
                            <CheckCircle className="h-4 w-4 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-sm text-gray-700">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Completion Section - Divider */}
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-300"></div>
                    </div>
                    <div className="relative flex justify-center">
                      <span className="px-3 bg-white text-sm text-gray-500">Complete Check</span>
                    </div>
                  </div>

                  {/* Form Fields */}
                  <MaintenanceCheckForm
                    formData={formData}
                    setFormData={setFormData}
                    relevantEquipment={relevantEquipment}
                    onShowAddEquipment={() => setShowAddEquipment(true)}
                    job={job}
                    crewList={crew}  // FT-221: Pass crew for "Completed By" dropdown
                  />

                  {/* Recent Completions Section */}
                  {completionHistory && completionHistory.length > 0 && (
                    <div className="mt-6 border-t pt-4">
                      <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                        <Clock className="h-4 w-4 text-gray-400" />
                        Recent Completions ({completionHistory.length > 5 ? '5 of ' + completionHistory.length : completionHistory.length})
                      </h3>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {completionHistory.slice(0, 5).map((completion, idx) => {
                          const isSignedOff = completion.signedOffStatus === 'signed_off';
                          const isPending = !isSignedOff && completion.signedOffStatus === 'pending';
                          return (
                          <div
                            key={completion.id || idx}
                            className="p-3 bg-gray-50 rounded-lg border border-gray-100"
                          >
                            <div className="flex items-center justify-between">
                              <div
                                onClick={() => handleViewCompletion(completion)}
                                className="flex items-center gap-3 flex-1 cursor-pointer hover:opacity-80 transition-opacity"
                              >
                                {completion.defectsFound ? (
                                  <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                                    <XCircle className="h-4 w-4 text-red-500" />
                                  </div>
                                ) : (
                                  <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                                    <CheckCircle className="h-4 w-4 text-green-500" />
                                  </div>
                                )}
                                <div>
                                  <span className="text-sm font-medium text-gray-900">
                                    {formatDate(completion.completedAt || completion.completedDate || completion.date)}
                                  </span>
                                  {/* FT-191: Resolve crew UUID to display name */}
                                  {/* FT-221: Pass completion for performerName check */}
                                  <div className="flex items-center gap-1 text-xs text-gray-500">
                                    <User className="h-3 w-3" />
                                    {getCrewDisplayName(completion.performedBy || completion.completedBy, completion)}
                                  </div>
                                  {/* FT-394: sign-off status */}
                                  {isSignedOff ? (
                                    <div className="flex items-center gap-1 text-xs text-green-700 mt-0.5">
                                      <Lock className="h-3 w-3" />
                                      Signed off by {completion.signedOffBy}
                                    </div>
                                  ) : isPending ? (
                                    <div className="flex items-center gap-1 text-xs text-amber-700 mt-0.5">
                                      <AlertTriangle className="h-3 w-3" />
                                      Awaiting sign-off
                                    </div>
                                  ) : null}
                                </div>
                              </div>
                              {/* FT-394: sign-off button — only skipper+ on pending records */}
                              {canSignOff && isPending && onSignOff && completion.id ? (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onSignOff(completion.id);
                                  }}
                                  disabled={isSigningOff}
                                  className="ml-2 inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {isSigningOff ? <Loader2 className="h-3 w-3 animate-spin" /> : <ShieldCheck className="h-3 w-3" />}
                                  Sign Off
                                </button>
                              ) : (
                                <ChevronRight className="h-4 w-4 text-gray-400 cursor-pointer" onClick={() => handleViewCompletion(completion)} />
                              )}
                            </div>
                          </div>
                          );
                        })}
                      </div>
                      {completionHistory.length > 5 && (
                        <button
                          type="button"
                          onClick={onViewHistory}
                          className="mt-2 text-sm text-cyan-600 hover:text-cyan-700 font-medium"
                        >
                          View all {completionHistory.length} completions →
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between">
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={onViewHistory}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <Clock className="h-4 w-4 mr-2" />
                      View History
                    </button>
                    {/* Delete button - only for custom jobs */}
                    {isCustomJob && onDelete && (
                      <button
                        type="button"
                        onClick={() => setShowDeleteConfirm(true)}
                        className="inline-flex items-center px-4 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete Job
                      </button>
                    )}
                  </div>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 text-sm font-medium text-white bg-cyan-600 border border-transparent rounded-md hover:bg-cyan-700"
                    >
                      Complete Check
                    </button>
                  </div>
                </div>
              </form>

              {/* Delete Confirmation Dialog */}
              {showDeleteConfirm && (
                <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-10">
                  <div className="bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl">
                    <div className="flex items-center mb-4">
                      <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center mr-3">
                        <Trash2 className="h-5 w-5 text-red-600" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">Delete Custom Job?</h3>
                    </div>
                    <p className="text-sm text-gray-600 mb-6">
                      Are you sure you want to delete "<strong>{job?.title || job?.equipmentType}</strong>"?
                      This action cannot be undone. Any history for this job will be preserved.
                    </p>
                    <div className="flex justify-end gap-3">
                      <button
                        type="button"
                        onClick={() => setShowDeleteConfirm(false)}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          onDelete(job.id);  // FT-182 fix: Pass job.id, not job object - archiveJob expects string ID
                          setShowDeleteConfirm(false);
                          onClose();
                        }}
                        className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700"
                      >
                        Delete Job
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Quick Add Equipment Modal */}
      <QuickAddEquipmentModal
        isOpen={showAddEquipment}
        onClose={() => setShowAddEquipment(false)}
        equipmentType={
          job?.equipmentType?.toLowerCase().includes('fire extinguisher') ? 'fire_extinguisher' :
          job?.equipmentType?.toLowerCase().includes('lifejacket') ? 'lifejacket' :
          job?.equipmentType?.toLowerCase().includes('liferaft') ? 'liferaft' :
          job?.equipmentType?.toLowerCase().includes('lifebuoy') ? 'lifebuoy' :
          job?.equipmentType?.toLowerCase().includes('first aid') ? 'first_aid_kit' :
          ''
        }
        onSave={(newEquipment) => {
          if (onAddEquipment) {
            onAddEquipment(newEquipment);
          }
          setShowAddEquipment(false);
          window.location.reload();
        }}
      />

      {/* Completion Report Full-Screen View */}
      {showCompletionReport && selectedCompletion && (
        <div className="fixed inset-0 z-[60] bg-white">
          <MaintenanceCompletionReport
            completion={selectedCompletion}
            job={job}
            equipment={equipment}
            onBack={() => {
              setShowCompletionReport(false);
              setSelectedCompletion(null);
            }}
            onEdit={handleEditCompletionFromModal}
          />
        </div>
      )}
    </>
  );
};

export default MaintenanceCheckModal;